import React from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { format } from 'date-fns';
import { Booking } from '../../types/booking';

interface ReservationsTableProps {
  selectedDate: Date | null;
  bookings: Booking[];
}

const ReservationsTable: React.FC<ReservationsTableProps> = ({
  selectedDate,
  bookings,
}) => {
  const getStatusColor = (
    status: string
  ): 'success' | 'warning' | 'error' | 'default' => {
    switch (status.toUpperCase()) {
      case 'CONFIRMED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  if (!selectedDate) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          Click on a bar in the chart to view bookings for that day
        </Typography>
      </Paper>
    );
  }

  if (bookings.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" mb={2}>
          Bookings for {format(selectedDate, 'MMMM dd, yyyy')}
        </Typography>
        <Typography color="text.secondary">
          No bookings found for this date
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" mb={2}>
        Bookings for {format(selectedDate, 'MMMM dd, yyyy')}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Customer Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Service</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Start Time</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bookings.map((booking, index) => {
              let startTimeDisplay = 'N/A';
              if (booking.start_time) {
                try {
                  const timeStr =
                    typeof booking.start_time === 'string'
                      ? booking.start_time
                      : format(booking.start_time, 'HH:mm');
                  // If it's already in HH:mm format, use it directly
                  startTimeDisplay = timeStr.includes(':')
                    ? timeStr.substring(0, 5)
                    : timeStr;
                } catch (error) {
                  console.error('Error formatting start time:', error);
                }
              }

              return (
                <TableRow key={booking.id || index}>
                  <TableCell>{booking.customer_name}</TableCell>
                  <TableCell>{booking.service_name || 'N/A'}</TableCell>
                  <TableCell>{startTimeDisplay}</TableCell>
                  <TableCell>
                    <Chip
                      label={booking.status}
                      color={getStatusColor(booking.status)}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default ReservationsTable;
